import {
  Badge,
  BodyText,
  Button,
  IconSet,
  Tooltip,
} from '@postscript/components';
import { useField } from 'formik';
import { useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { logButtonClickEvent } from 'utils/events';

interface FormOptionProps {
  disabled?: boolean;
  selected: boolean;
}

const StyledFormOption = styled.label<FormOptionProps>`
  align-items: flex-start;
  background-color: var(--surface-bkg-color);
  border-radius: var(--border-radius-large);
  box-shadow: 0 0 0 1px var(--border-color-dim);
  cursor: pointer;
  display: block;
  gap: var(--spacing-2);
  margin-bottom: 0;
  opacity: ${({ disabled }) => (disabled ? '0.8' : '1')};
  overflow-x: auto;
  // padding: var(--spacing-4) var(--spacing-5) var(--spacing-5);
  padding: var(--spacing-3);
  position: relative;
  transition: box-shadow var(--hover-transition);

  :focus-within {
    box-shadow: 0 0 0 1.5px var(--highlight-color), var(--box-shadow-small);
  }

  && {
    ${({ selected }) =>
      selected
        ? `
          box-shadow: 0 0 0 3px var(--highlight-color),
            0 0 5px 3px var(--highlight-color);
        `
        : `
          :hover {
            box-shadow: 0 0 0 1.5px var(--highlight-color),
              var(--box-shadow-small);
          }
      `}
  }

  :last-child {
    margin-bottom: 0;
  }

  label {
    --radio-margin: 0px;
  }
`;

const StyledOptionInput = styled.input`
  clip-path: inset(50%);
  clip: rect(0 0 0 0);
  height: 1px;
  overflow: hidden;
  margin: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const StyledOptionTitleWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  gap: var(--spacing-2);
  justify-content: space-between;
`;

const StyledOptionTitle = styled(BodyText)`
  color: var(--text-color);
  font-weight: var(--body-text-bold-font-weight);
  margin-bottom: 0;
`;

const StyledButton = styled(Button)`
  margin: calc(var(--spacing-1) * -1) calc(var(--spacing-1) * -1) 0 0;
  min-height: 0;
  padding: var(--spacing-1) !important;
  width: auto;
`;

const StyledTooltipContent = styled.div`
  white-space: nowrap;
`;

const StyledOptionDescription = styled(BodyText)`
  margin: var(--spacing-2) 0 0;
`;

const StyledOptionBadge = styled(Badge)`
  margin-top: var(--spacing-2);
`;

interface OptionProps {
  description: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  tag: string;
  title: string;
  value: number;
}
const UNCLICKED_TEXT = 'Copy shareable link to clipboard';
const CLICKED_TEXT = 'Copied!';

export default function ChooseFlowTemplateOption({
  description,
  onChange,
  tag,
  title,
  value,
}: OptionProps) {
  const { url } = useRouteMatch();
  const [text, setText] = useState(UNCLICKED_TEXT);
  const tooltipId = `template-link-tooltip-${value}`;

  const [field, , helpers] = useField<number>('templateId');

  return (
    <StyledFormOption selected={field.value === value}>
      <StyledOptionInput
        checked={field.value === value}
        name={field.name}
        onChange={(e) => {
          helpers.setValue(parseInt(e.currentTarget.value));
          onChange(e);
        }}
        tabIndex={0}
        type="radio"
        value={value}
      />
      <StyledOptionTitleWrapper>
        <StyledOptionTitle size="medium">{title}</StyledOptionTitle>
        <StyledButton
          aria-label={UNCLICKED_TEXT}
          data-for={tooltipId}
          data-tip
          icon={IconSet.Link}
          onClick={(event) => {
            navigator.clipboard.writeText(
              `${window.location.origin}${url.replace(
                'new',
                '',
              )}templates/${value}`,
            );
            setText(CLICKED_TEXT);
            logButtonClickEvent(event, { flow_template_id: value });
          }}
          onMouseLeave={() => setText(UNCLICKED_TEXT)}
          variant="text"
          size="large"
        />
        <Tooltip id={tooltipId} place="right" size="small">
          <StyledTooltipContent>{text}</StyledTooltipContent>
        </Tooltip>
      </StyledOptionTitleWrapper>
      {description && (
        <StyledOptionDescription size="medium">
          {description}
        </StyledOptionDescription>
      )}
      {tag && <StyledOptionBadge variant="info">{tag}</StyledOptionBadge>}
    </StyledFormOption>
  );
}
